import { CartItem } from "../types";

type ToastLevel = "info" | "success" | "warning";

export interface PocToast {
  id: string;
  message: string;
  level: ToastLevel;
}

export interface PocNotification {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  level: ToastLevel;
}

export interface PocOrderHistoryItem {
  id: string;
  createdAt: string;
  total: number;
  itemsCount: number;
  itemsLabel: string;
}

const NOTIFICATIONS_KEY = "boba_poc_notifications_v1";
const ORDER_HISTORY_KEY = "boba_poc_order_history_v1";

const toId = () =>
  `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const safeParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const canUseDOM = () => typeof window !== "undefined";

const emit = (eventName: string, detail?: unknown) => {
  if (!canUseDOM()) return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

const saveNotifications = (items: PocNotification[]) => {
  if (!canUseDOM()) return;
  window.localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(items));
};

const saveOrderHistory = (items: PocOrderHistoryItem[]) => {
  if (!canUseDOM()) return;
  window.localStorage.setItem(ORDER_HISTORY_KEY, JSON.stringify(items));
};

export const getNotifications = (): PocNotification[] => {
  if (!canUseDOM()) return [];
  return safeParse<PocNotification[]>(
    window.localStorage.getItem(NOTIFICATIONS_KEY),
    [],
  );
};

export const getUnreadNotificationsCount = (): number =>
  getNotifications().filter((n) => !n.read).length;

export const addNotification = ({
  title,
  message,
  level = "info",
}: {
  title: string;
  message: string;
  level?: ToastLevel;
}) => {
  if (!canUseDOM()) return;
  const current = getNotifications();
  const next: PocNotification = {
    id: toId(),
    title,
    message,
    createdAt: new Date().toISOString(),
    read: false,
    level,
  };
  saveNotifications([next, ...current].slice(0, 60));
  emit("boba:notifications-updated");
};

export const markAllNotificationsRead = () => {
  const current = getNotifications();
  if (!current.length) return;
  saveNotifications(current.map((n) => ({ ...n, read: true })));
  emit("boba:notifications-updated");
};

export const markNotificationRead = (id: string) => {
  const current = getNotifications();
  saveNotifications(current.map((n) => (n.id === id ? { ...n, read: true } : n)));
  emit("boba:notifications-updated");
};

export const pushToast = (message: string, level: ToastLevel = "info") => {
  emit("boba:toast", {
    id: toId(),
    message,
    level,
  } satisfies PocToast);
};

export const getOrderHistory = (): PocOrderHistoryItem[] => {
  if (!canUseDOM()) return [];
  return safeParse<PocOrderHistoryItem[]>(
    window.localStorage.getItem(ORDER_HISTORY_KEY),
    [],
  );
};

export const addOrderToHistory = (cart: CartItem[]) => {
  if (!cart.length || !canUseDOM()) return;

  const total = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const itemNames = [...new Set(cart.map((item) => item.product.name))];

  const entry: PocOrderHistoryItem = {
    id: `ORD-${new Date().getTime().toString().slice(-6)}`,
    createdAt: new Date().toISOString(),
    total,
    itemsCount: cart.length,
    itemsLabel:
      itemNames.length > 2
        ? `${itemNames[0]}, ${itemNames[1]}...`
        : itemNames.join(", "),
  };

  const current = getOrderHistory();
  saveOrderHistory([entry, ...current].slice(0, 40));
  emit("boba:history-updated");
};

export const seedPocData = () => {
  if (!canUseDOM()) return;

  const existingNotifs = getNotifications();
  if (!existingNotifs.length) {
    saveNotifications([
      {
        id: toId(),
        title: "Bienvenue sur le POC",
        message: "Le centre de notifications est prêt.",
        createdAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        read: false,
        level: "info",
      },
      {
        id: toId(),
        title: "Stock faible",
        message: "2 produits sont en stock faible (simulation).",
        createdAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        read: true,
        level: "warning",
      },
    ]);
  }

  const existingHistory = getOrderHistory();
  if (!existingHistory.length) {
    saveOrderHistory([
      {
        id: "ORD-102938",
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
        total: 12.4,
        itemsCount: 2,
        itemsLabel: "Brown Sugar Milk Tea, Matcha Cookie",
      },
      {
        id: "ORD-102674",
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 27).toISOString(),
        total: 6.5,
        itemsCount: 1,
        itemsLabel: "Mango Pomelo Sago",
      },
    ]);
  }
};

export const formatRelativeTime = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days} j`;
};
